import { Button, ButtonGroup } from "@progress/kendo-react-buttons";
import { SvgIcon, useDraggable, type NormalizedDragEvent } from "@progress/kendo-react-common";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { Input, type InputChangeEvent } from "@progress/kendo-react-inputs";
import { searchIcon } from "@progress/kendo-svg-icons";
import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useListScenariosQuery } from "../../../api/scenarioApi";
import type { ListScenariosRequest, ScenarioDto } from "../../../contracts";
import { isCommercialSimulatorSubBrandToolResponse, isCommercialSimulatorToolResponse } from "../../../models/toolResponseModels";
import { formatDate } from "../../../utils/dateValueConversion";
import { truncateString } from "../../../utils/stringValueConversion";

type ScenarioLibraryMetric = {
    label: string;
    value: string;
};

export type ScenarioLibraryScenario = {
    id: number;
    title: string;
    retailer: string;
    date: string;
    tag: string;
    description: string;
    createdByUser: string;
    metrics: ScenarioLibraryMetric[];
};

const subBrands = ["Remedy Kombucha"];

type ScenarioLibraryListScenariosRequest = ListScenariosRequest & {
    search_term?: string | null;
};

type ScenarioLibraryProps = {
    selectedScenarioIds: number[];
    draggingScenarioId: number | null;
    onScenarioDragStart: (scenario: ScenarioLibraryScenario) => void;
    onScenarioDragEnd: () => void;
};

type ScenarioLibraryArticleProps = {
    scenario: ScenarioLibraryScenario;
    isSelected: boolean;
    isDragging: boolean;
    onDragStart: (scenario: ScenarioLibraryScenario) => void;
    onDragEnd: () => void;
};

const getScenarioInfo = (input?: unknown) => {
    if (isCommercialSimulatorToolResponse(input)) {
        return {
            retailer: input.tool_call_user_selection.retailer_name,
            objective: input.tool_call.input?.objective
        };
    }

    // Not exactly correct, should really be the user response but cant be done now due to structure.
    if (isCommercialSimulatorSubBrandToolResponse(input)) {
        return {
            retailer: input.tool_call.input?.retailers?.join(", "),
            objective: input.tool_call.input?.objective
        };
    }

    return null;
};

const toScenarioLibraryScenario = (scenario: ScenarioDto): ScenarioLibraryScenario => {
    const metricSeed: number = scenario.id % 7;
    const info = getScenarioInfo(scenario.input);

    return {
        id: scenario.id,
        title: scenario.name,
        retailer: info?.retailer ?? "Retailer Unavailable",
        date: formatDate(scenario.updated_at),
        tag: info?.objective ?? "Objective not available",
        description: scenario.description || "Description not available",
        createdByUser: scenario.created_by_user,
        metrics: [
            { label: "Revenue", value: `+${8 + metricSeed}%` },
            { label: "Profit", value: `+${10 + metricSeed}%` },
            { label: "ROI", value: `+${5 + metricSeed}%` }
        ]
    };
};

type DragPoint = {
    x: number;
    y: number;
};

type DragPreviewState = {
    left: number;
    top: number;
    width: number;
    height: number;
};

type ScenarioLibraryArticleContentProps = {
    scenario: ScenarioLibraryScenario;
    isSelected: boolean;
};

const ScenarioLibraryArticleContent: React.FC<ScenarioLibraryArticleContentProps> = ({ scenario, isSelected }) => (
    <>
        <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
            <h3 className="h6 fw-bold text-body mb-0" style={{ fontSize: "14px" }}>{scenario.title}</h3>
            {isSelected && (
                <span className="badge text-bg-primary rounded-pill flex-shrink-0">Selected</span>
            )}
        </div>
        <div className="d-flex align-items-center gap-2 small text-secondary mb-2">
            <span>{scenario.retailer}</span>
            <span aria-hidden="true">•</span>
            <span>{scenario.date}</span>
        </div>

        <span
            className="badge rounded-1 fw-bold mb-2 text-start"
            style={{ backgroundColor: "#27836f", color: "#ffffff" }}
        >
            {truncateString(scenario.description, 30)}
        </span>

        <div className="row g-0">
            {scenario.metrics.map(metric => (
                <div className="col-4" key={`${scenario.title}-${metric.label}`}>
                    <div className="small text-secondary mb-1" style={{ fontSize: "12px" }}>{metric.label}</div>
                    <div className="fw-semibold" style={{ color: "#006a57", fontSize: "12px" }}>{metric.value}</div>
                </div>
            ))}
        </div>
    </>
);

const getScenarioArticleStyles = (isDragging: boolean, isSelected: boolean, isPressed: boolean): CSSProperties => ({
    borderColor: isDragging ? "#0d6efd" : "#dce4ef",
    boxShadow: isDragging ? "0 0 0 0.25rem rgba(13, 110, 253, 0.18)" : "0 1px 1px rgba(15, 23, 42, 0.02)",
    cursor: isPressed ? "grabbing" : "grab",
    opacity: isSelected ? 0.55 : 1,
    backgroundColor: isDragging ? "#f8fbff" : "#ffffff",
    transform: isDragging ? "scale(1.01)" : "none"
});

const ScenarioLibraryArticle: React.FC<ScenarioLibraryArticleProps> = ({ scenario, isSelected, isDragging, onDragStart, onDragEnd }) => {
    const articleRef = useRef<HTMLElement | null>(null);
    const previewRef = useRef<HTMLElement | null>(null);
    const pointerOffsetRef = useRef<DragPoint>({ x: 0, y: 0 });
    const initialPointerRef = useRef<DragPoint>({ x: 0, y: 0 });
    const initialPreviewPositionRef = useRef<DragPoint>({ x: 0, y: 0 });
    const [isPressed, setIsPressed] = useState<boolean>(false);
    const [dragPreview, setDragPreview] = useState<DragPreviewState | null>(null);

    const resetDragPreview = (): void => {
        pointerOffsetRef.current = { x: 0, y: 0 };
        initialPointerRef.current = { x: 0, y: 0 };
        initialPreviewPositionRef.current = { x: 0, y: 0 };
        setIsPressed(false);
        setDragPreview(null);
    };

    useDraggable(articleRef, {
        onPress: (event: NormalizedDragEvent): void => {
            const articleBounds = articleRef.current?.getBoundingClientRect();
            pointerOffsetRef.current = articleBounds
                ? {
                        x: event.clientX - articleBounds.left,
                        y: event.clientY - articleBounds.top
                    }
                : { x: event.offsetX, y: event.offsetY };
            setIsPressed(true);
        },
        onRelease: (): void => {
            setIsPressed(false);
        },
        onDragStart: (event: NormalizedDragEvent): void => {
            const articleBounds = articleRef.current?.getBoundingClientRect();
            const previewWidth = articleBounds?.width ?? 260;
            const previewHeight = articleBounds?.height ?? 160;
            const initialLeft = event.clientX - pointerOffsetRef.current.x;
            const initialTop = event.clientY - pointerOffsetRef.current.y;

            initialPointerRef.current = { x: event.clientX, y: event.clientY };
            initialPreviewPositionRef.current = { x: initialLeft, y: initialTop };
            setDragPreview({
                left: initialLeft,
                top: initialTop,
                width: previewWidth,
                height: previewHeight
            });
            onDragStart(scenario);
        },
        onDrag: (event: NormalizedDragEvent): void => {
            const left = initialPreviewPositionRef.current.x + event.clientX - initialPointerRef.current.x;
            const top = initialPreviewPositionRef.current.y + event.clientY - initialPointerRef.current.y;

            setDragPreview(currentPreview => currentPreview
                ? {
                        ...currentPreview,
                        left: left,
                        top: top
                    }
                : null);
        },
        onDragEnd: (): void => {
            resetDragPreview();
            onDragEnd();
        }
    }, { hint: previewRef });

    return (
        <>
            <article
                ref={articleRef}
                className="rounded-3 border border-2 p-3 user-select-none"
                style={getScenarioArticleStyles(isDragging, isSelected, isPressed)}
                aria-label={`${scenario.title} draggable scenario`}
            >
                <ScenarioLibraryArticleContent scenario={scenario} isSelected={isSelected} />
            </article>

            {dragPreview && createPortal(
                <article
                    ref={previewRef}
                    className="rounded-3 border border-2 p-3"
                    style={{
                        ...getScenarioArticleStyles(true, isSelected, true),
                        position: "fixed",
                        left: `${dragPreview.left}px`,
                        top: `${dragPreview.top}px`,
                        width: `${dragPreview.width}px`,
                        minHeight: `${dragPreview.height}px`,
                        zIndex: 2000,
                        pointerEvents: "none"
                    }}
                    aria-hidden="true"
                >
                    <ScenarioLibraryArticleContent scenario={scenario} isSelected={isSelected} />
                </article>,
                document.body
            )}
        </>
    );
};

const ScenarioLibrary: React.FC<ScenarioLibraryProps> = ({ selectedScenarioIds, draggingScenarioId, onScenarioDragStart, onScenarioDragEnd }) => {
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");

    useEffect(() => {
        const timeoutId: number = window.setTimeout(() => {
            setDebouncedSearchTerm(searchTerm.trim());
        }, 300);

        return () => window.clearTimeout(timeoutId);
    }, [searchTerm]);

    const request: ScenarioLibraryListScenariosRequest = {
        skip: 0,
        take: 5,
        search_term: debouncedSearchTerm || null
    };

    const { data, isError, isFetching, isLoading } = useListScenariosQuery(request);
    const scenarios: ScenarioLibraryScenario[] = useMemo(
        () => data?.data.map(toScenarioLibraryScenario) ?? [],
        [data?.data]
    );
    const totalCount: number = data?.total ?? 0;
    const handleSearchChange = (event: InputChangeEvent): void => {
        setSearchTerm(event.value as string);
    };

    return (
        <aside
            className="d-flex flex-column h-100 bg-white border-end"
            aria-label="Scenario Library"
        >
            <header className="px-3 pt-3 pb-4 border-bottom">
                <h2 className="h5 fw-bold text-body mb-2">Scenario Library</h2>
                <p className="small text-secondary mb-0">Browse and compare saved scenarios</p>
            </header>

            <section className="px-3 py-3 border-bottom" aria-label="Scenario filters">
                <label className="form-label small fw-semibold text-secondary mb-2" htmlFor="scenario-library-sub-brand">
                    Sub Brand
                </label>

                <DropDownList
                    id="scenario-library-sub-brand"
                    className="w-100 mb-3"
                    data={subBrands}
                    defaultValue="Remedy Kombucha"
                    rounded="large"
                    style={{ height: "39px" }}
                />

                <div className="position-relative mb-3">
                    <span
                        className="position-absolute top-50 translate-middle-y text-secondary d-inline-flex align-items-center justify-content-center"
                        style={{ left: "12px", zIndex: 2 }}
                        aria-hidden="true"
                    >
                        <SvgIcon icon={searchIcon} size="medium" />
                    </span>
                    <Input
                        className="w-100"
                        placeholder="Search by scenario name..."
                        style={{ height: "39px", paddingLeft: "36px", borderRadius: "10px" }}
                        value={searchTerm}
                        onChange={handleSearchChange}
                    />
                </div>

                <ButtonGroup className="w-100 rounded-3 p-1 bg-body-tertiary border-0">
                    <Button
                        selected={true}
                        fillMode="flat"
                        className="flex-fill fw-semibold shadow-sm border-0 rounded-3"
                        style={{ backgroundColor: "#ffffff", color: "#001433", fontSize: "12px" }}
                    >
                        My Scenarios
                    </Button>
                    <Button
                        fillMode="flat"
                        className="flex-fill border-0 rounded-3 text-secondary"
                        style={{ fontSize: "12px" }}
                    >
                        Team Scenarios
                    </Button>
                </ButtonGroup>
            </section>

            <div className="px-3 py-2 border-bottom bg-body-tertiary">
                <p className="small text-secondary mb-0">
                    {totalCount}
                    {" "}
                    scenarios found
                </p>
            </div>

            <section className="d-flex flex-column gap-3 p-3" aria-label="Saved scenarios">
                {(isLoading || isFetching) && (
                    <div className="rounded-3 border bg-body-tertiary p-3 small text-secondary">Loading scenarios...</div>
                )}

                {isError && (
                    <div className="rounded-3 border border-danger-subtle bg-danger-subtle p-3 small text-danger">Unable to load scenarios.</div>
                )}

                {!isLoading && !isFetching && !isError && scenarios.length === 0 && (
                    <div className="rounded-3 border bg-body-tertiary p-3 small text-secondary">No scenarios match your search.</div>
                )}

                {!isLoading && !isFetching && !isError && scenarios.map(scenario => (
                    <ScenarioLibraryArticle
                        key={scenario.id}
                        scenario={scenario}
                        isSelected={selectedScenarioIds.includes(scenario.id)}
                        isDragging={draggingScenarioId === scenario.id}
                        onDragStart={onScenarioDragStart}
                        onDragEnd={onScenarioDragEnd}
                    />
                ))}
            </section>
        </aside>
    );
};

export default ScenarioLibrary;
